import { DeleteOutlined, WarningOutlined } from '@ant-design/icons';
import {
  EnumRichiestaDTOStato,
  RichiestaDTO,
  getGetFrontRichiesteQueryKey,
  useDeleteFrontRichiesteId,
} from '@framework/api-client';
import { getNetworkErrorMessage } from '@framework/auth';
import { useTranslation } from '@framework/i18n';
import { ButtonDeleteConfirm } from '@framework/ui';
import { Button, Modal, Tooltip, message, theme } from 'antd';
import { useCallback } from 'react';
import { queryClient } from '../../../core/network/queryClient';
const { useToken } = theme;

const canDelete = ({ stato }: RichiestaDTO) => {
  return (
    stato === EnumRichiestaDTOStato.CREATA ||
    stato === EnumRichiestaDTOStato.BOZZA
  );
};

export interface ISubmissionDeleteButtonProps {
  submission?: RichiestaDTO;
  typeModal?: boolean;
}

export function SubmissionDeleteButton(props: ISubmissionDeleteButtonProps) {
  const { submission, typeModal } = props;
  const { t } = useTranslation();
  const [modal, contextHolder] = Modal.useModal();

  const deleteMutation = useDeleteFrontRichiesteId({
    mutation: {
      onSuccess: () => {
        message.success(
          t(
            'submission-list.richiesta-eliminata-con-successo',
            'Richiesta eliminata con successo',
          ),
        );
        queryClient.invalidateQueries(getGetFrontRichiesteQueryKey());
      },
      onError: error => {
        message.error(
          getNetworkErrorMessage(
            error,
            t(
              'submission-list.errore-eliminazione-richiesta',
              "Errore durante l'eliminazione della richiesta",
            ),
          ),
        );
      },
    },
  });

  const { token } = useToken();
  const submissionId = submission?.id;

  const modalDeleteText = t(
    'submission.button.button-delete-modal.elimina-richiesta',
    'Elimina richiesta',
  );

  const handleModalDelete = useCallback(() => {
    if (!submissionId) {
      message.error(t("submission.button.button-delete-modal.la-richiesta-deve-essere-salvata-per-essere-eliminata", `La richiesta deve essere salvata per poter essere eliminata.`)); // prettier-ignore
      return;
    }

    modal.confirm({
      title: t(
        'submission.button.button-delete-modal.sei-sicuro-di-voler-eliminare-la-richiesta',
        'Sei sicuro di voler eliminare la richiesta?',
      ),
      icon: (
        <WarningOutlined
          aria-hidden="true"
          style={{ color: token.colorError }}
        />
      ),
      type: 'error',
      okButtonProps: {
        danger: true,
        // 'aria-label': modalDeleteText,
      },
      okText: modalDeleteText,
      cancelText: t(
        'submission.button.button-delete-modal.modal-button.chiudi',
        'Chiudi',
      ),
      async onOk() {
        deleteMutation.mutateAsync({ id: submissionId }).catch(err => {
          console.error(err);
        });
      },
      onCancel() {},
    });
  }, [
    token.colorError,
    t,
    deleteMutation,
    submissionId,
    modal,
    modalDeleteText,
  ]);

  const handleDelete = (submission: RichiestaDTO) => {
    deleteMutation.mutateAsync({ id: submission.id! }).catch(err => {
      console.error(err);
    });
  };
  if (!submission) return null;
  if (!canDelete(submission)) return null;

  return (
    <>
      {typeModal ? (
        <>
          {contextHolder}
          <Tooltip title={modalDeleteText}>
            <Button
              size="small"
              type="text"
              danger
              icon={<DeleteOutlined aria-label={modalDeleteText} />}
              onClick={handleModalDelete}
            />
          </Tooltip>
        </>
      ) : (
        <ButtonDeleteConfirm
          iconOnlyButton
          buttonType="text"
          buttonDanger
          onConfirm={() => handleDelete(submission)}
        />
      )}
    </>
  );
}
